// apps/web/src/widgets/scaling-matrix/renderer.tsx
//
// Phase 11.4 — REND-06 / REND-07 / REND-08 ScalingMatrixRenderer.
// Generalizes the size-and-yield single-axis renderer to TWO interacting axes
// plus a typeable+stepper integer multiplier with fractional additional-base
// semantics, an optional predefined-series sub-picker, and a computed per-pass
// yield. RHF-free: reads `config` + RecipeStateContext `state`, writes via
// `onStateChange` (auto-persisted by the 11.3 provider).
//
// Skeleton REUSED from size-and-yield/renderer.tsx: the Card › CardContent ›
// <section role="group"> › <h3> shell, the `flex flex-wrap gap-2` Pill grid
// (BOTH axis rows), the single-select "click-same = no-op" swap, and the
// verbatim `aria-live="polite"` success / "Yield: —" fallback pair. The yield
// string is produced by the SAME `formatYield` helper (imported, NOT
// re-implemented — `ScalingMatrixYieldDisplay` is field-compatible with
// `SizeAndYieldYieldDisplay`); the only scaling-specific input is the
// `perPassYield(...)` multiplier from compute-scaling.ts.
//
// NEW surfaces (UI-SPEC §"New Component" is the contract, no in-repo analog):
//   - Multiplier stepper [−][Input][+]: composed from Button + Input + Button
//     (no stepper primitive exists). Four states — Typeable / Clamped (floor +
//     clamp via clampMultiplier) / Invalid-revert (empty|NaN on blur → revert to
//     last-good, no state mutation) / Greyed (state.series set → native disabled,
//     value retained). Bounds come from config.multiplier.{primary|secondary}
//     per the active axis-role — NEVER hard-coded (REND-08).
//   - Predefined-series sub-picker: a nested <section role="group"> with a series
//     Pill row + an index Pill row (1..indexMax) + a generic clear-series Pill.
//     Active series greys the stepper; the sub-picker is then the editor.
//   - "= X.YZ× base equivalent" sublabel from effectiveBaseEquivalent(...); "—"
//     on a non-finite value.
//
// GENERALITY (C1): no `16`/`.25`/`.5`/`ladle`/`oz`/`Full`/`pancake` literal. All // grep-allow: pancake, oz, ladle
// numbers (bounds, scale, base fractions) read from config/state; the only copy
// strings are recipe-generic ("Pick a size", "No sizes to pick from.", "base
// equivalent", "Preset series", "Off"). Unit words come from
// yieldDisplay.unitsField recipe DATA via formatYield.
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pill } from "@/components/ui/pill";
import { formatYield } from "../size-and-yield/format-yield.js";
import type { WidgetRendererProps } from "../widget-renderer-props.js";
import {
  clampMultiplier,
  effectiveBaseEquivalent,
  perPassYield,
  seriesEffective,
} from "./compute-scaling.js";

// The multiplier control governs the PRIMARY axis-role (the dominant scaling
// dimension): its bounds are config.multiplier.primary and its base-equivalent
// is the multiplier itself (effectiveBaseEquivalent primary-role branch). The
// non-primary branch is exercised by the compute unit tests; the live surface
// is the primary role.
const STEPPER_IS_PRIMARY_ROLE = true as const;

export function ScalingMatrixRenderer({
  widget,
  config,
  state,
  onStateChange,
}: WidgetRendererProps<"scaling-matrix">): React.ReactElement {
  // State narrowing (Plan 01 type-map): the scaling variant or undefined. A
  // stale persisted step id that no longer exists in an axis degrades to the
  // pre-selection UI for that axis.
  const axisSelections: Readonly<Record<string, string>> = state?.axisSelections ?? {};
  const multiplier: number = state?.multiplier ?? config.multiplier.primary.default;
  const activeSeries = state?.series;

  const primaryAxis = config.axes[0];
  const secondaryAxis = config.axes[1];

  const primaryStep =
    primaryAxis !== undefined
      ? primaryAxis.steps.find((s) => s.id === axisSelections[primaryAxis.id])
      : undefined;
  const secondaryStep =
    secondaryAxis !== undefined
      ? secondaryAxis.steps.find((s) => s.id === axisSelections[secondaryAxis.id])
      : undefined;

  // Bounds for the stepper per the active axis-role (REND-08) — recipe-declared,
  // never literal.
  const bounds = STEPPER_IS_PRIMARY_ROLE ? config.multiplier.primary : config.multiplier.secondary;

  const labelId = `${widget.instanceId}-label`;
  const inputId = `${widget.instanceId}-multiplier`;
  const seriesLabelId = `${widget.instanceId}-series-label`;

  // Draft text for the typeable multiplier. Kept local so an in-progress edit
  // (empty / partial / out-of-range) does not mutate persisted state until it
  // commits (blur / Enter). Re-syncs whenever the committed multiplier changes
  // (e.g. a ± click, or a series clear restoring the retained value).
  const [draft, setDraft] = React.useState<string>(String(multiplier));
  React.useEffect(() => {
    setDraft(String(multiplier));
  }, [multiplier]);

  const seriesActive = activeSeries !== undefined;

  // Single-select axis swap: clicking the already-selected step is a no-op (a
  // size cannot be unselected in v1). Preserves the OTHER axis + multiplier +
  // series (only axisSelections changes).
  const onPick = (axisId: string, stepId: string): void => {
    if (axisSelections[axisId] === stepId) return;
    onStateChange({
      axisSelections: { ...axisSelections, [axisId]: stepId },
      multiplier,
      ...(activeSeries !== undefined ? { series: activeSeries } : {}),
    });
  };

  // Commit a typed multiplier: floor + clamp via clampMultiplier. A non-finite
  // result (empty / NaN) is the "revert-to-last-good" sentinel — restore the
  // draft to the committed multiplier and do NOT mutate state (REND-08).
  //
  // parseInt(base 10) is the correct integer parse: it rejects the non-decimal
  // numeric forms a plain integer field must not accept (`Number("0x10")` → 16,
  // `Number("1e3")` → 1000, `Number("0b11")` → 3 all coerce; parseInt stops at
  // the first non-digit). An empty / non-numeric draft → NaN, which is the
  // revert sentinel clampMultiplier maps to undefined, so the empty-string
  // special-case collapses into the NaN path.
  const commitDraft = (): void => {
    const parsed = Number.parseInt(draft.trim(), 10);
    const clamped = clampMultiplier(parsed, bounds);
    if (clamped === undefined) {
      setDraft(String(multiplier));
      return;
    }
    setDraft(String(clamped));
    if (clamped !== multiplier) {
      onStateChange({
        axisSelections,
        multiplier: clamped,
        ...(activeSeries !== undefined ? { series: activeSeries } : {}),
      });
    }
  };

  // ± steppers: clamp the neighbour value (the disabled state at a bound already
  // prevents crossing, but clamp defensively).
  const stepBy = (delta: number): void => {
    const next = clampMultiplier(multiplier + delta, bounds);
    if (next === undefined || next === multiplier) return;
    onStateChange({
      axisSelections,
      multiplier: next,
      ...(activeSeries !== undefined ? { series: activeSeries } : {}),
    });
  };

  // Series sub-picker writers.
  const onPickSeriesIndex = (seriesId: string, index: number): void => {
    onStateChange({ axisSelections, multiplier, series: { id: seriesId, index } });
  };
  const onClearSeries = (): void => {
    // Drop `series` entirely; the stepper re-enables at its retained value.
    onStateChange({ axisSelections, multiplier });
  };

  // Effective "× base equivalent" — series-derived when a series is active,
  // otherwise the live multiplier's effective value.
  const seriesDef =
    activeSeries !== undefined
      ? config.predefinedSeries?.find((s) => s.id === activeSeries.id)
      : undefined;
  const effectiveValue =
    activeSeries !== undefined && seriesDef !== undefined
      ? seriesEffective(seriesDef, activeSeries.index)
      : primaryStep !== undefined
        ? effectiveBaseEquivalent({
            primaryStep,
            multiplier,
            isPrimaryRole: STEPPER_IS_PRIMARY_ROLE,
          })
        : undefined;

  // Computed per-pass yield via the REUSED formatYield. perPassYield needs both
  // resolved axis steps; until both are picked the yield falls back to "Yield: —".
  //
  // [Rule 1 - composition] formatYield resolves its `{value}` token as
  // `baseValue × multiplier` (its size-and-yield contract — the arg is a per-step
  // FRACTION, not a final count). perPassYield already returns the FINAL per-pass
  // count (e.g. the reference 16). Feeding that count straight in would
  // double-count (baseValue 4 × 16 = 64). To REUSE formatYield purely for token /
  // unit substitution while displaying perPassYield verbatim, pass
  // `perPassYield / baseValue` so `{value}` resolves back to perPassYield. Guard a
  // 0/non-finite baseValue → fall back to "Yield: —".
  const computedYield =
    primaryStep !== undefined && secondaryStep !== undefined
      ? perPassYield({
          primaryStep,
          secondaryStep,
          yieldRule: config.yieldDisplay.yieldRule,
          baseValue: config.yieldDisplay.baseValue,
        })
      : undefined;
  const yieldString =
    computedYield !== undefined &&
    Number.isFinite(config.yieldDisplay.baseValue) &&
    config.yieldDisplay.baseValue > 0
      ? formatYield(config.yieldDisplay, computedYield / config.yieldDisplay.baseValue)
      : undefined;

  const renderAxisRow = (axis: typeof primaryAxis): React.ReactElement => {
    if (axis === undefined || axis.steps.length === 0) {
      return <p className="text-sm text-[var(--recipe-muted)]">No sizes to pick from.</p>;
    }
    const selectedId = axisSelections[axis.id];
    return (
      <>
        {selectedId === undefined ? (
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--recipe-muted)]">
            Pick a size
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {axis.steps.map((step) => (
            <Pill
              key={step.id}
              selected={axisSelections[axis.id] === step.id}
              disabled={false}
              onClick={() => {
                onPick(axis.id, step.id);
              }}
            >
              {step.label}
            </Pill>
          ))}
        </div>
      </>
    );
  };

  return (
    <Card className="mb-8">
      <CardContent className="p-4">
        {/* biome-ignore lint/a11y/useSemanticElements: section role="group" matches Phase 6 option-set convention; a native <fieldset> would inject legend styling and constrain inner layout. */}
        <section role="group" aria-labelledby={labelId} className="flex flex-col gap-2">
          <h3
            id={labelId}
            className="text-base font-semibold leading-tight text-[var(--recipe-ink)]"
          >
            {widget.name}
          </h3>

          {/* (1) Primary axis Pill row */}
          {renderAxisRow(primaryAxis)}

          {/* (2) Secondary axis Pill row — same size-and-yield visual */}
          {renderAxisRow(secondaryAxis)}

          {/* (3) Multiplier stepper [−][Input][+] + base-equivalent sublabel */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Label htmlFor={inputId} className="sr-only">
                Multiplier
              </Label>
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label="Decrease multiplier"
                disabled={seriesActive || multiplier <= bounds.min}
                onClick={() => {
                  stepBy(-1);
                }}
              >
                {"−"}
              </Button>
              <Input
                id={inputId}
                type="number"
                step="1"
                min={bounds.min}
                max={bounds.max}
                className="w-20 text-center"
                disabled={seriesActive}
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                }}
                onBlur={() => {
                  commitDraft();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    commitDraft();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label="Increase multiplier"
                disabled={seriesActive || multiplier >= bounds.max}
                onClick={() => {
                  stepBy(1);
                }}
              >
                {"+"}
              </Button>
            </div>
            {/* No aria-live here: the computed-yield region below (REND-07) is
                the single announced surface. The base-equivalent sublabel
                mirrors the same committed edit, so making it live too would
                double-announce on every change. */}
            <p className="text-xs text-[var(--recipe-muted)]">
              {effectiveValue !== undefined
                ? `= ${effectiveValue.toLocaleString("en-US")}× base equivalent`
                : "= —× base equivalent"}
            </p>
          </div>

          {/* (4) Predefined-series sub-picker — only when the recipe declares series */}
          {config.predefinedSeries !== undefined && config.predefinedSeries.length > 0 ? (
            // biome-ignore lint/a11y/useSemanticElements: nested section role="group" matches the Phase 6 widget-grouping convention used above; a native <fieldset> would inject legend styling.
            <section role="group" aria-labelledby={seriesLabelId} className="flex flex-col gap-2">
              <p
                id={seriesLabelId}
                className="text-xs font-semibold uppercase tracking-wider text-[var(--recipe-muted)]"
              >
                Preset series
              </p>
              <div className="flex flex-wrap gap-2">
                <Pill
                  selected={!seriesActive}
                  disabled={false}
                  onClick={() => {
                    onClearSeries();
                  }}
                >
                  Off
                </Pill>
                {config.predefinedSeries.map((series) => (
                  <Pill
                    key={series.id}
                    selected={activeSeries?.id === series.id}
                    disabled={false}
                    onClick={() => {
                      // Selecting a series defaults to its first index (1).
                      onPickSeriesIndex(series.id, 1);
                    }}
                  >
                    {series.label}
                  </Pill>
                ))}
              </div>
              {seriesActive && seriesDef !== undefined ? (
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: seriesDef.indexMax }, (_, i) => i + 1).map((index) => (
                    <Pill
                      key={index}
                      selected={activeSeries?.index === index}
                      disabled={false}
                      onClick={() => {
                        onPickSeriesIndex(seriesDef.id, index);
                      }}
                    >
                      {String(index)}
                    </Pill>
                  ))}
                </div>
              ) : null}
            </section>
          ) : null}

          {/* (5) Computed per-pass yield (aria-live) */}
          {yieldString !== undefined ? (
            <p aria-live="polite" className="mt-3 text-sm font-semibold text-[var(--recipe-ink)]">
              {yieldString}
            </p>
          ) : (
            <p aria-live="polite" className="mt-3 text-sm text-[var(--recipe-muted)]">
              Yield: —
            </p>
          )}
        </section>
      </CardContent>
    </Card>
  );
}
